// frontend/src/App.js
import React, { useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import axios from "./axiosConfig";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import socket from "./socket";
import {
  playNewTradeSound,
  playGeneralNotificationSound,
} from "./utils/notificationSound";
import notificationAxios from "./notificationAxios";

// NEW: import extracted Navbar
import Navbar from "./components/Navbar";

// ✅ MAINTENANCE MODE: new page import
import Maintenance from "./pages/Maintenance";

// --- Import your pages/components ---
import Home from "./pages/Home";
import QuickTrade from "./pages/QuickTrade";
import Exchange from "./pages/Exchange";
import About from "./pages/About";
import Support from "./pages/Support";
import Market from "./pages/Market";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import VerifyOtp from "./pages/VerifyOtp";
import VerifyLoginOtp from "./pages/VerifyLoginOtp";
import Dashboard from "./pages/Dashboard";
import Deposit from "./pages/Deposit";
import Loader from "./components/Loader";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import WalletPage from "./pages/WalletPage";
import Withdraw from "./pages/Withdraw";
import MyProfile from "./pages/MyProfile";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import MarketsOverview from "./pages/MarketsOverview";
import TradePage from "./pages/TradePage";
import Fees from "./pages/Fees";
import LearnOverview from "./pages/LearnOverview";
import TradeSmart from "./pages/TradeSmart";
import Glossary from "./pages/Glossary";
import Blog from "./pages/Blog";
import TermsOfUse from "./pages/TermsOfUse";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AmlKycPolicy from "./pages/AmlKycPolicy";
import ComingSoon from "./pages/ComingSoon";
import PostAd from "./pages/PostAd";
import P2PTradeChat from "./pages/P2PTradeChat";
import MyP2PTrades from "./pages/MyP2PTrades";
import P2PHub from "./pages/P2PHub";
import EditAd from "./pages/EditAd";
import P2PProfile from "./pages/P2PProfile";
import PublicP2PProfile from "./pages/PublicP2PProfile";
import SettingsNotifications from "./pages/SettingsNotifications";
import Verify from "./pages/Verify";

// ───────────────────────────────────────────────────────────────────────────────
// ✅ MAINTENANCE MODE: read flag from env (build-time)
const MAINTENANCE_MODE =
  (process.env.REACT_APP_MAINTENANCE_MODE || "").toLowerCase() === "true";

// ✅ Optional bypass token (also build-time). If set, you can bypass by adding ?bypass=TOKEN
const MAINTENANCE_BYPASS_TOKEN = process.env.REACT_APP_MAINTENANCE_BYPASS || "";

// ✅ Optional: allow admin pages even during maintenance
// Set to "true" if you want /admin/login and /admin/dashboard accessible during maintenance.
const MAINTENANCE_ALLOW_ADMIN =
  (process.env.REACT_APP_MAINTENANCE_ALLOW_ADMIN || "").toLowerCase() === "true";

// ───────────────────────────────────────────────────────────────────────────────
// AnimatedRoutes (unchanged)
function AnimatedRoutes() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(t);
  }, [location.pathname]);

  return (
    <>
      {loading && <Loader />}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/support" element={<Support />} />
          <Route path="/market" element={<Market />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/verify-login-otp" element={<VerifyLoginOtp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute requireKyc={false}>
                <MyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verify"
            element={
              <ProtectedRoute requireKyc={false}>
                <Verify />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/markets" element={<MarketsOverview />} />
          <Route path="/trade" element={<TradePage />} />
          <Route path="/fees" element={<Fees />} />
          <Route path="/learn" element={<LearnOverview />} />
          <Route path="/trade-smart" element={<TradeSmart />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/aml-kyc" element={<AmlKycPolicy />} />

          <Route path="/careers" element={<ComingSoon />} />
          <Route path="/partners" element={<ComingSoon />} />
          <Route path="/corporate-account" element={<ComingSoon />} />
          <Route path="/api-docs" element={<ComingSoon />} />
          <Route path="/download" element={<ComingSoon />} />
          <Route path="/trade-contest" element={<ComingSoon />} />
          <Route path="/white-paper" element={<ComingSoon />} />
          <Route path="/list-crypto" element={<ComingSoon />} />
          <Route path="/post-ad" element={<PostAd />} />

          {/* P2P */}
          <Route
            path="/p2p/trade/:tradeId"
            element={
              <ProtectedRoute>
                <P2PTradeChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p/my-trades"
            element={
              <ProtectedRoute>
                <MyP2PTrades />
              </ProtectedRoute>
            }
          />
          <Route
            path="/p2p"
            element={
              <ProtectedRoute>
                <P2PHub />
              </ProtectedRoute>
            }
          />
          <Route path="/p2p/edit-ad/:id" element={<EditAd />} />
          <Route path="/p2p/profile" element={<P2PProfile />} />
          <Route path="/p2p/user/:username" element={<PublicP2PProfile />} />

          {/* Protected */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quick-trade"
            element={
              <ProtectedRoute>
                <QuickTrade />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exchange"
            element={
              <ProtectedRoute>
                <Exchange />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deposit"
            element={
              <ProtectedRoute>
                <Deposit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/withdraw"
            element={
              <ProtectedRoute>
                <Withdraw />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet/*"
            element={
              <ProtectedRoute>
                <WalletPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <SettingsNotifications />
              </ProtectedRoute>
            }
          />
        </Routes>
      </AnimatePresence>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
function App() {
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ always declare hooks first (no early returns before hooks)
  const notifiedSetRef = useRef(new Set());

  // ✅ MAINTENANCE MODE: bypass check
  const bypassParam =
    new URLSearchParams(window.location.search).get("bypass") || "";
  const hasBypass =
    (process.env.REACT_APP_MAINTENANCE_BYPASS || "") &&
    bypassParam === process.env.REACT_APP_MAINTENANCE_BYPASS;

  // ✅ MAINTENANCE MODE: allow admin paths if enabled
  const MAINTENANCE_MODE =
    (process.env.REACT_APP_MAINTENANCE_MODE || "").toLowerCase() === "true";
  const MAINTENANCE_ALLOW_ADMIN =
    (process.env.REACT_APP_MAINTENANCE_ALLOW_ADMIN || "").toLowerCase() ===
    "true";

  const isAdminPath = location.pathname.startsWith("/admin");
  const maintenanceActive =
    MAINTENANCE_MODE && !(hasBypass || (MAINTENANCE_ALLOW_ADMIN && isAdminPath));

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const res = await notificationAxios.get("/notifications");
      const all = res.data.notifications || [];
      setNotifications(all);
      setUnread(all.filter((n) => !n.read));
    } catch (err) {
      console.error("❌ Failed to fetch notifications", err);
    }
  };

  // Re-fetch when user logs in
  useEffect(() => {
    if (maintenanceActive) return;
    if (user) fetchNotifications();
  }, [user, maintenanceActive]);

  // Socket listeners + sounds
  useEffect(() => {
    if (maintenanceActive) return;

    const token = localStorage.getItem("token");
    if (!user || !token || socket.connected) return;

    socket.auth = { token };
    socket.connect();

    const presencePing = () => {
      try {
        socket.emit("presence:ping");
      } catch {}
    };
    const presenceInterval = setInterval(presencePing, 20000);

    socket.on("connect", () => {
      if (user?._id) socket.emit("join-room", user._id);
      presencePing();
    });

    const notifiedSet = notifiedSetRef.current;

    let lastDing = 0;
    const throttleMs = 600;
    const soundOnce = (key, soundFn) => {
      const now = Date.now();
      if (now - lastDing < throttleMs) return;
      lastDing = now;
      if (!notifiedSet.has(key)) {
        notifiedSet.add(key);
        soundFn && soundFn();
      }
    };

    const pushNotification = (data, soundKey, soundFn) => {
      const isTradeStartSystem =
        (data?.isSystem || data?.type === "system" || data?.role === "system") &&
        (data?.systemKind === "trade_start" ||
          /trade started/i.test(data?.message || ""));

      if (!isTradeStartSystem) {
        soundOnce(soundKey, soundFn);
      }
      fetchNotifications();
    };

    socket.on("p2p:new-trade", (data) => {
      pushNotification(data, `new-trade-${data.tradeId}`, playNewTradeSound);
    });
    socket.on("p2p:trade-paid", (data) => {
      pushNotification(
        data,
        `trade-paid-${data.tradeId}`,
        playGeneralNotificationSound
      );
    });
    socket.on("p2p:trade-cancelled", (data) => {
      pushNotification(
        data,
        `trade-cancelled-${data.tradeId}`,
        playGeneralNotificationSound
      );
    });
    socket.on("p2p:trade-released", (data) => {
      pushNotification(
        data,
        `trade-released-${data.tradeId}`,
        playGeneralNotificationSound
      );
    });
    socket.on("p2p:trade-expiring", (data) => {
      pushNotification(
        data,
        `trade-expiring-${data.tradeId}`,
        playGeneralNotificationSound
      );
    });
    socket.on("p2p:trade-expired", (data) => {
      pushNotification(
        data,
        `trade-expired-${data.tradeId}`,
        playGeneralNotificationSound
      );
    });
    socket.on("p2p:dispute-raised", (data) => {
      pushNotification(
        data,
        `dispute-opened-${data.tradeId}`,
        playGeneralNotificationSound
      );
    });
    socket.on("p2p:dispute-cancelled", (data) => {
      pushNotification(
        data,
        `dispute-cancelled-${data.tradeId}`,
        playGeneralNotificationSound
      );
    });
    socket.on("p2p:dispute-resolved", (data) => {
      pushNotification(
        data,
        `dispute-resolved-${data.tradeId}`,
        playGeneralNotificationSound
      );
    });

    socket.on("p2p:new-message", (data) => {
      if (data.sender?._id === user?._id) return;
      pushNotification(
        data,
        `new-message-${data.trade}`,
        playGeneralNotificationSound
      );
    });

    socket.on("notification:new", () => {
      playGeneralNotificationSound();
      fetchNotifications();
    });
    socket.on("notifications:new", () => {
      playGeneralNotificationSound();
      fetchNotifications();
    });

    return () => {
      clearInterval(presenceInterval);

      socket.off("connect");
      socket.off("p2p:new-trade");
      socket.off("p2p:trade-paid");
      socket.off("p2p:trade-cancelled");
      socket.off("p2p:trade-released");
      socket.off("p2p:trade-expiring");
      socket.off("p2p:trade-expired");
      socket.off("p2p:new-message");
      socket.off("p2p:dispute-raised");
      socket.off("p2p:dispute-cancelled");
      socket.off("p2p:dispute-resolved");
      socket.off("notification:new");
      socket.off("notifications:new");
      socket.disconnect();
    };
  }, [user, maintenanceActive]);

  // Fetch current user on route change
  useEffect(() => {
    if (maintenanceActive) return;

    axios
      .get("/whoami")
      .then((res) => setUser(res.data.user))
      .catch(() => setUser(null));
  }, [location.pathname, maintenanceActive]);

  // Visitor logging
  useEffect(() => {
    if (maintenanceActive) return;
    axios.post("/visitor/log").catch(console.error);
  }, [maintenanceActive]);

  // Hardened logout using the SAME variable name
  const handleLogout = () => {
    try {
      axios.post("/logout").catch(() => {});
      localStorage.removeItem("token");
      localStorage.removeItem("verifyEmail");
      localStorage.removeItem("signupEmail");

      delete axios.defaults.headers.common?.Authorization;
      if (notificationAxios?.defaults?.headers?.common?.Authorization) {
        delete notificationAxios.defaults.headers.common.Authorization;
      }

      try {
        socket.disconnect();
      } catch {}

      setUser(null);
      setNotifications([]);
      setUnread([]);
      setShowNotifDropdown(false);
      setShowProfileDropdown(false);
      notifiedSetRef.current = new Set();

      navigate("/");
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  // ✅ Final render: show maintenance without breaking hook rules
  if (maintenanceActive) {
    return <Maintenance />;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Navbar
        user={user}
        onLogout={handleLogout}
        unread={unread}
        setUnread={setUnread}
        showNotifDropdown={showNotifDropdown}
        setShowNotifDropdown={setShowNotifDropdown}
        showProfileDropdown={showProfileDropdown}
        setShowProfileDropdown={setShowProfileDropdown}
        notifications={notifications}
        fetchNotifications={fetchNotifications}
      />

      <ToastContainer position="top-right" autoClose={3000} />
      <main className="flex-grow">
        <AnimatedRoutes />
      </main>
      <Footer />
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────────────
export default function WrappedApp() {
  return (
    <Router>
      <App />
    </Router>
  );
}
